import clsx from 'clsx'
import React, {useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {CSSTransition} from 'react-transition-group'
import {ConfirmActionModal} from '../../../../../gori/components'
import {useCancelToken, UsePermission} from '../../../../../gori/hooks'
import {useA1BatchesProvider} from '../../../../../gori/providers'
import {SelectFormik, TableHeader, ValidationErrorModal} from '../../../../../gori/widgets'
import {A1_CONSTANTS, A1BatchService, A1Helpers, MassUpdateModal} from '../../../a1'
import {A1BatchesDetailTableFilter} from './A1BatchesDetailTableFilter'
import {useA1OrderFilter} from '../../hooks'
import {useSearchParams} from 'react-router-dom'
import {CreateLabelButton} from './CreateLabelButton'
import {PrintLabelButton} from './PrintLabelButton'
import {isEmpty, reduce} from 'lodash'
import {OrderStatus} from '../../../../../gori/constants'

type Props = {
  selectedList?: any
  reloadTable: any
  isVisibleCheckbox: boolean
  isVisiblePrintLabel?: boolean
  isVisibleCreateLabel?: boolean
}

const A1BatchesDetailFilter: React.FC<Props> = ({
  selectedList,
  reloadTable,
  isVisibleCheckbox = false,
  isVisiblePrintLabel = false,
  isVisibleCreateLabel = false,
}) => {
  const intl = useIntl()
  const {newCancelToken, isCancel} = useCancelToken()
  const {isOrigin, isDestination} = UsePermission()
  const {a1Batch} = useA1BatchesProvider()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState<{
    upload: boolean
    export: boolean
    removeOrderA1Batch: boolean
  }>({
    upload: false,
    export: false,
    removeOrderA1Batch: false,
  })
  const [validationErrors, setValidationErrors] = useState<any>()
  const [selectAll, setSelectAll] = useState<boolean>(false)
  const [dataModal, setDataModal] = useState<{
    massUpdate: {
      selectAll: boolean
      selectedList: any
      typeMassUpdate: string
      releaseStatus?: string
    } | null
  }>({
    massUpdate: null,
  })
  const [showModal, setShowModal] = useState<{
    massUpdate: boolean
    removeFromA1Batch: boolean
  }>({
    massUpdate: false,
    removeFromA1Batch: false,
  })

  const {filters} = useA1OrderFilter()

  useEffect(() => {
    if (!selectedList.selectedMaster) {
      setSelectAll(false)
    }
  }, [selectedList.selected, selectedList.selectedMaster])

  const isVisiblePrintLabelBySelected = useMemo(() => {
    if (!isVisiblePrintLabel) return false

    return selectedList.selected.some((item) => !isEmpty(item.original.tracking_numbers))
  }, [isVisiblePrintLabel, selectedList.selected])

  const isVisibleCreateLabelBySelected = useMemo(() => {
    if (!isVisibleCreateLabel) return false

    return selectedList.selected.some((item) => isEmpty(item.original.tracking_numbers))
  }, [isVisibleCreateLabel, selectedList.selected])

  const showMassUpdate = useMemo(() => {
    return A1Helpers.checkJourneyStatus({
      statusCurrent: a1Batch?.journey_status,
      statusCheck: A1_CONSTANTS.OPTIONS_JOURNEY_STATUS.CUSTOMS.value[0],
      comparisonType: 'less_than_or_equal',
    })
  }, [a1Batch?.journey_status])

  const handleExport = async () => {
    try {
      setLoading((prev) => ({...prev, export: true}))
      const response: any = await A1BatchService.downloadOrdersA1Batch(a1Batch.id, {
        cancelToken: newCancelToken(),
        params: {
          order_tab: searchParams.get('order_tab') || undefined,
          ...filters,
        },
      })
      if (response) {
        const blob = new Blob([response.content], {type: response.type})
        let link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = response.file_name
        link.click()
      }
    } catch (error: any) {
      if (isCancel(error)) return
      setValidationErrors(error?.response)
    } finally {
      setLoading((prev) => ({...prev, export: false}))
    }
  }

  const handleMassUpdate = (option) => {
    setShowModal((prev) => ({...prev, massUpdate: true}))
    setDataModal((prev) => ({
      ...prev,
      massUpdate: {
        selectAll: selectAll,
        selectedList: selectedList,
        typeMassUpdate: option.value,
      },
    }))
  }

  const selectedOpenOrderIds = useMemo(() => {
    return reduce(
      selectedList.selected,
      (result: number[], item) => {
        if (item.original.status === OrderStatus.OPEN) {
          result.push(item.original.sb_order_id)
        }
        return result
      },
      []
    )
  }, [selectedList.selected])

  const selectedOrderIds = useMemo(
    () => selectedList.selected.map((item) => item.original.sb_order_id),
    [selectedList.selected]
  )

  // Orders already released by customs can no longer be removed from the batch.
  const hasReleasedOrderSelected = useMemo(() => {
    const releasedStatuses = [
      A1_CONSTANTS.OPTIONS_RELEASE_STATUS.FORMAL_RELEASED.value,
      A1_CONSTANTS.OPTIONS_RELEASE_STATUS.INFORMAL_RELEASED.value,
      A1_CONSTANTS.OPTIONS_RELEASE_STATUS.MANUAL_RELEASED.value,
    ]

    return selectedList.selected.some((item) =>
      releasedStatuses.includes(item.original.release_status)
    )
  }, [selectedList.selected])

  const isVisibleOtherActions = useMemo(
    () => isOrigin && !!a1Batch?.is_yamato && !!selectedList.selected.length,
    [isOrigin, a1Batch?.is_yamato, selectedList.selected.length]
  )

  const otherActionsOptions = useMemo(
    () => [
      {
        label: intl.formatMessage({id: 'REMOVE_FROM_A1_BATCH'}),
        value: 'removeFromA1Batch',
        isDisabled: hasReleasedOrderSelected,
      },
    ],
    [intl, hasReleasedOrderSelected]
  )

  const handleCloseRemoveModal = () => {
    setShowModal((prev) => ({...prev, removeFromA1Batch: false}))
  }

  const handleRemoveOrderA1Batch = async () => {
    try {
      setLoading((prev) => ({...prev, removeOrderA1Batch: true}))
      const {message}: any = await A1BatchService.removeOrdersA1Batch(
        a1Batch.id,
        selectedOrderIds,
        {
          cancelToken: newCancelToken(),
        }
      )
      if (message) {
        toast.success(intl.formatMessage({id: 'REMOVED_ORDERS_SUCCESSFULLY'}))
        handleCloseRemoveModal()
        reloadTable()
      }
    } catch (error: any) {
      if (isCancel(error)) return
      setValidationErrors(error?.response)
    } finally {
      setLoading((prev) => ({...prev, removeOrderA1Batch: false}))
    }
  }

  return (
    <>
      <A1BatchesDetailTableFilter
        className={clsx({
          'mb-5': !selectedList.selected.length,
        })}
        rightComponent={
          <div className='gap-5 d-flex ms-4'>
            <div
              className={clsx(
                'text-gray-700 text-hover-primary cursor-pointer fw-bolder text-decoration-underline'
              )}
              onClick={handleExport}
            >
              {intl.formatMessage({id: 'EXPORT_AS_CSV'})}
              {loading.export && (
                <span className='spinner-border spinner-border-sm align-middle ms-2' />
              )}
            </div>
          </div>
        }
      />
      <div>
        {validationErrors && (
          <ValidationErrorModal
            handleClose={() => {
              setValidationErrors(undefined)
            }}
            response={validationErrors}
          />
        )}
        {showModal.massUpdate && (
          <MassUpdateModal
            show={showModal.massUpdate}
            handleClose={() => {
              setShowModal((prev) => ({...prev, massUpdate: false}))
              setDataModal((prev) => ({...prev, massUpdate: null}))
            }}
            data={dataModal.massUpdate}
            reloadTable={() => {
              reloadTable()
              setShowModal((prev) => ({...prev, massUpdate: false}))
              setDataModal((prev) => ({...prev, massUpdate: null}))
            }}
          />
        )}
        {showModal.removeFromA1Batch && (
          <ConfirmActionModal
            show={showModal.removeFromA1Batch}
            title={intl.formatMessage({id: 'REMOVE_FROM_A1_BATCH'})}
            message={intl.formatMessage({
              id: 'ARE_YOU_SURE_YOU_WANT_TO_REMOVE_THE_SELECTED_ORDERS_FROM_THIS_A1_BATCH',
            })}
            messagePrimary={intl.formatMessage({id: 'SUBMIT'})}
            messageSecondary={intl.formatMessage({id: 'CANCEL'})}
            handlePrimaryAction={handleRemoveOrderA1Batch}
            loadingPrimary={loading.removeOrderA1Batch}
            handleSecondaryAction={handleCloseRemoveModal}
            handleClose={handleCloseRemoveModal}
          />
        )}
        <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
          <TableHeader className='min-h-1px border-0 d-flex align-items-center justify-content-between mb-5'>
            {isVisibleCheckbox && selectedList.selected.length > 0 && (
              <div
                className={clsx(
                  'd-flex justify-content-end align-items-center flex-wrap gap-5 mt-3'
                )}
              >
                <div className='fw-bolder'>
                  <span className='me-2'>
                    {selectAll ? selectedList.total.length : selectedList.selected.length}
                  </span>
                  {intl.formatMessage({id: 'SELECTED'})}
                </div>
                {isVisibleCreateLabelBySelected && (
                  <CreateLabelButton
                    batchId={a1Batch.id}
                    orderIds={selectedOpenOrderIds}
                    onSuccessCallback={reloadTable}
                  />
                )}
                {isVisiblePrintLabelBySelected && (
                  <PrintLabelButton
                    batchId={a1Batch.id}
                    orderIds={selectedList.selected.map((item) => item.original.sb_order_id)}
                    onSuccessCallback={reloadTable}
                    onErrorCallback={(error) => setValidationErrors(error?.response)}
                  />
                )}
                {isVisibleOtherActions && (
                  <SelectFormik
                    className={clsx('w-200px fw-bold fs-5')}
                    value={{label: intl.formatMessage({id: 'OTHER_ACTIONS'}), value: ''}}
                    options={otherActionsOptions}
                    emptyDefault={false}
                    onChange={(e: any) => {
                      switch (e.value) {
                        case 'removeFromA1Batch':
                          setShowModal((prev) => ({...prev, removeFromA1Batch: true}))
                          break
                        default:
                          break
                      }
                    }}
                  />
                )}
                {isDestination && (
                  <>
                    {!selectAll && selectedList.selectedMaster && (
                      <div
                        onClick={() => setSelectAll(true)}
                        className='fw-bolder me-5 link-blue text-decoration-underline cursor-pointer'
                      >
                        {intl.formatMessage({id: 'SELECT_ALL'})} {selectedList.total.length}
                      </div>
                    )}
                    {selectAll && (
                      <div
                        onClick={() => setSelectAll(false)}
                        className='fw-bolder me-5 link-blue text-decoration-underline cursor-pointer'
                      >
                        {intl.formatMessage({id: 'CLEAR_SELECTION'})}
                      </div>
                    )}
                    {showMassUpdate && selectedList?.selected?.length && (
                      <SelectFormik
                        className={clsx('w-200px fw-bold fs-5')}
                        value={{label: intl.formatMessage({id: 'MASS_UPDATE'}), value: ''}}
                        options={Object.values(A1_CONSTANTS.MASS_UPDATE)}
                        onChange={handleMassUpdate}
                        hasUseIntl={true}
                        emptyDefault={false}
                      />
                    )}
                  </>
                )}
              </div>
            )}
          </TableHeader>
        </CSSTransition>
      </div>
    </>
  )
}

export {A1BatchesDetailFilter}
